import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import {
  NetworkStatus,
  OperationVariables,
  TypedDocumentNode,
} from "@apollo/client";

import {
  RiskManagementMetricType,
  SystemOverviewMetricType,
} from "$/__generated__/graphql";

import type { ColumnSort, Connection } from "./types";

/*
  Infinite scrolling can exhibit subtle bugs with viewport heights. If the table fits neatly into the viewport,
  there won't be any scroll events to trigger fetchMore. We can auto fetch the next page, just in case. For now,
  keep an eye out on this in testing, and we can add an auto-fetch if needed.
*/

const FIRST_PAGE_SIZE = 50;

const toOrderInputs = (
  sort: Array<ColumnSort>,
): Array<Record<string, "ASC" | "DESC">> =>
  sort.map(({ id, desc }) => ({ [id]: desc ? "DESC" : "ASC" }));

export const useDataTable = <
  T,
  Q extends Record<string, unknown> = Record<string, unknown>,
  V extends OperationVariables = OperationVariables,
>(
  queryName: keyof Q,
  tableDocument: TypedDocumentNode<Q, V>,
  options?: useQuery.Options<Q, V>,
) => {
  const [showFoot, setShowFoot] = useState(false);

  const [selectionMetric, setSelectionMetric] =
    useState<SystemOverviewMetricType>(SystemOverviewMetricType.Mode);

  const [systemMetric, setSystemMetric] = useState<SystemOverviewMetricType>(
    SystemOverviewMetricType.Mode,
  );

  const [riskSelectionMetric, setRiskSelectionMetric] =
    useState<RiskManagementMetricType>(RiskManagementMetricType.Median);

  const [riskSystemMetric, setRiskSystemMetric] =
    useState<RiskManagementMetricType>(RiskManagementMetricType.Median);

  const [sorting, setSorting] = useState<Array<ColumnSort>>([]);

  const order = sorting.length ? toOrderInputs(sorting) : undefined;

  const tableResult = useQuery(tableDocument, {
    ...options!,
    variables: {
      first: FIRST_PAGE_SIZE,
      order,
      ...options?.variables,
    },
  });

  const connection = tableResult.data?.[queryName] as Connection<T> | undefined;
  const { hasNextPage, endCursor } = connection?.pageInfo ?? {};

  const fetchMoreDataTable = useCallback(() => {
    if (tableResult.loading) {
      return;
    }

    if (!hasNextPage) {
      return;
    }

    void tableResult.fetchMore({
      variables: {
        after: endCursor,
      } as V["variables"],
    });
  }, [endCursor, hasNextPage, tableResult]);

  // we may need a separate loader for fetching more; for now it fetches silently
  const isFetchingMore = tableResult.networkStatus === NetworkStatus.fetchMore;

  return useMemo(
    () => ({
      ...tableResult,
      showFoot,
      setShowFoot,
      selectionMetric,
      setSelectionMetric,
      systemMetric,
      setSystemMetric,
      riskSelectionMetric,
      setRiskSelectionMetric,
      riskSystemMetric,
      setRiskSystemMetric,
      rows: connection?.edges?.map((edge) => edge.node) ?? [],
      isFetchingMore,
      fetchMoreDataTable,
      sorting,
      setSorting,
    }),
    [
      connection,
      fetchMoreDataTable,
      isFetchingMore,
      riskSelectionMetric,
      riskSystemMetric,
      selectionMetric,
      showFoot,
      systemMetric,
      tableResult,
      sorting,
      setSorting,
    ],
  );
};
